import React from 'react';
import { useAuth } from '../../contexts/AuthContext';

/**
 * PermissionGuard component for protecting UI elements based on permissions
 * 
 * @param {Object} props - Component props
 * @param {string|string[]} props.permission - Required permission(s)
 * @param {string|string[]} props.role - Required role(s) (alternative to permission)
 * @param {number} props.minRoleLevel - Minimum role level required
 * @param {boolean} props.requireAll - If true, user must have ALL permissions (default: false)
 * @param {React.ReactNode} props.children - Content to render if user has permission
 * @param {React.ReactNode} props.fallback - Content to render if user lacks permission
 * @param {boolean} props.showFallback - Whether to show fallback content (default: false)
 * @param {Function} props.onUnauthorized - Callback when user lacks permission
 */
const PermissionGuard = ({
  permission,
  permissions, // alias for permission
  role,
  minRoleLevel,
  requireAll = false,
  children,
  fallback = null,
  showFallback = false,
  onUnauthorized
}) => {
  // Support both `permission` and `permissions` props
  if (!permission && permissions) {
    permission = permissions;
  }
  const { 
    user, 
    isAuthenticated, 
    hasPermission, 
    hasRole, 
    hasRoleLevel,
    isSuperAdmin
  } = useAuth();

  // If not authenticated, don't render anything unless showFallback is true
  if (!isAuthenticated || !user) {
    if (onUnauthorized) {
      onUnauthorized('not_authenticated');
    }
    return showFallback ? fallback : null;
  }

  // SUPER_ADMIN always has full access automatically without explicit permission assignment
  const isSuperAdminUser = (typeof isSuperAdmin === 'function' && isSuperAdmin()) || hasRole('SUPER_ADMIN');
  if (isSuperAdminUser) {
    return children;
  }

  // ADMIN role has full access to all non-HRM components without explicit permission assignment
  const isAdminUser = hasRole('ADMIN');
  if (isAdminUser) {
    const isHrmPermissionCode = (p) => {
      const code = String(typeof p === 'string' ? p : p?.code || '').toUpperCase();
      return code.startsWith('HRM:') || code.startsWith('HR:');
    };

    if (permission) {
      const perms = Array.isArray(permission) ? permission : [permission];
      const hasHrm = requireAll
        ? perms.some(isHrmPermissionCode)
        : perms.every(isHrmPermissionCode);
      if (hasHrm) {
        if (onUnauthorized) {
          onUnauthorized('insufficient_permissions');
        }
        return showFallback ? fallback : null;
      }
    }

    if (role) {
      const targetRoles = (Array.isArray(role) ? role : [role]).map((r) => String(r || '').toUpperCase());
      const isHrmRole = targetRoles.every((r) => r.includes('HRM') || r === 'HR');
      if (isHrmRole) {
        if (onUnauthorized) {
          onUnauthorized('insufficient_permissions');
        }
        return showFallback ? fallback : null;
      }
    }

    return children;
  }

  let hasAccess = false;

  // Check permission-based access
  if (permission) {
    if (Array.isArray(permission)) {
      hasAccess = requireAll 
        ? permission.every(p => hasPermission(p))
        : permission.some(p => hasPermission(p));
    } else {
      hasAccess = hasPermission(permission);
    }
  }
  
  // Check role-based access (if no permission check or as fallback)
  if (!hasAccess && role) {
    hasAccess = hasRole(role);
  }
  
  // Check role level access
  else if (!hasAccess && minRoleLevel !== undefined) {
    hasAccess = hasRoleLevel(minRoleLevel);
  }
  
  // Default to allowing access if no restrictions specified
  else if (!permission && !role && minRoleLevel === undefined) {
    hasAccess = true;
  }

  if (!hasAccess) {
    if (onUnauthorized) {
      onUnauthorized('insufficient_permissions');
    }
    return showFallback ? fallback : null;
  }

  return children;
};

/**
 * Higher-order component version of PermissionGuard
 */
export const withPermissionGuard = (permission, options = {}) => {
  return (WrappedComponent) => {
    const GuardedComponent = (props) => (
      <PermissionGuard 
        permission={permission} 
        {...options}
        showFallback={true}
        fallback={
          <div className="flex items-center justify-center p-8">
            <div className="text-center">
              <div className="text-gray-500 mb-2">
                <svg className="mx-auto h-12 w-12" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                </svg>
              </div>
              <h3 className="text-lg font-medium text-gray-900 mb-1">Access Restricted</h3>
              <p className="text-sm text-gray-500">
                You don't have permission to access this content.
              </p>
            </div>
          </div>
        }
      >
        <WrappedComponent {...props} />
      </PermissionGuard>
    );

    GuardedComponent.displayName = `withPermissionGuard(${WrappedComponent.displayName || WrappedComponent.name})`;
    return GuardedComponent;
  };
};

/**
 * Hook for checking permissions in functional components
 */
export const usePermissionCheck = () => {
  const { hasPermission, hasRole, hasRoleLevel, user } = useAuth();

  const checkPermission = (permission, requireAll = false) => {
    if (!permission) return true;
    
    if (Array.isArray(permission)) {
      return requireAll 
        ? permission.every(p => hasPermission(p))
        : permission.some(p => hasPermission(p));
    }
    
    return hasPermission(permission);
  };

  const checkRole = (role) => {
    if (!role) return true;
    return hasRole(role);
  };

  const checkRoleLevel = (minLevel) => {
    if (minLevel === undefined) return true;
    return hasRoleLevel(minLevel);
  };

  const getUserPermissions = () => {
    return user?.permissions || [];
  };

  const getUserRoles = () => {
    return user?.roles || [];
  };

  const getPrimaryRole = () => {
    if (!user?.roles) return null;
    return user.roles.find(r => r.isPrimary) || user.roles[0] || null;
  };

  return {
    checkPermission,
    checkRole,
    checkRoleLevel,
    getUserPermissions,
    getUserRoles,
    getPrimaryRole,
    hasPermission,
    hasRole,
    hasRoleLevel
  };
};

/**
 * Component for conditional rendering based on multiple permission checks
 */
export const ConditionalRender = ({ children }) => {
  const { hasPermission, hasRole, hasRoleLevel } = useAuth();

  const processChildren = (child) => {
    if (!React.isValidElement(child)) return child;

    const { permission, role, minRoleLevel, requireAll, ...props } = child.props;

    if (!permission && !role && minRoleLevel === undefined) {
      return child;
    }

    let hasAccess = false;

    if (permission) {
      if (Array.isArray(permission)) {
        hasAccess = requireAll 
          ? permission.every(p => hasPermission(p))
          : permission.some(p => hasPermission(p));
      } else {
        hasAccess = hasPermission(permission);
      }
    } else if (role) {
      hasAccess = hasRole(role);
    } else if (minRoleLevel !== undefined) {
      hasAccess = hasRoleLevel(minRoleLevel);
    }

    if (!hasAccess) return null;

    return React.cloneElement(child, props);
  };

  return (
    <>
      {React.Children.map(children, processChildren)}
    </>
  );
};

/**
 * Button component with built-in permission checking
 */
export const PermissionButton = ({
  permission,
  role,
  minRoleLevel,
  requireAll = false,
  disabled = false,
  disabledMessage = "You don't have permission to perform this action",
  children,
  onClick,
  className = '',
  ...props
}) => {
  const { hasPermission, hasRole, hasRoleLevel } = useAuth();

  let hasAccess = true;

  if (permission) {
    if (Array.isArray(permission)) {
      hasAccess = requireAll 
        ? permission.every(p => hasPermission(p))
        : permission.some(p => hasPermission(p));
    } else {
      hasAccess = hasPermission(permission);
    }
  } else if (role) {
    hasAccess = hasRole(role);
  } else if (minRoleLevel !== undefined) {
    hasAccess = hasRoleLevel(minRoleLevel);
  }

  const isDisabled = disabled || !hasAccess;

  const handleClick = (e) => {
    if (!hasAccess) {
      e.preventDefault();
      if (disabledMessage) {
        // You could integrate with a toast system here
        console.warn(disabledMessage);
      }
      return;
    }
    
    if (onClick) {
      onClick(e);
    }
  };

  return (
    <button
      {...props}
      disabled={isDisabled}
      onClick={handleClick}
      className={`${className} ${isDisabled ? 'opacity-50 cursor-not-allowed' : ''}`}
      title={!hasAccess ? disabledMessage : props.title}
    >
      {children}
    </button>
  );
};

/**
 * Menu item component with permission checking
 */
export const PermissionMenuItem = ({
  permission,
  role,
  minRoleLevel,
  requireAll = false,
  children,
  onClick,
  className = '',
  ...props
}) => {
  const permissionCheck = usePermissionCheck();

  const hasAccess = permission
    ? permissionCheck.checkPermission(permission, requireAll)
    : role
    ? permissionCheck.checkRole(role)
    : minRoleLevel !== undefined
    ? permissionCheck.checkRoleLevel(minRoleLevel)
    : true;

  if (!hasAccess) {
    return null;
  }

  return (
    <div
      {...props}
      onClick={onClick}
      className={className}
    >
      {children}
    </div>
  );
};

export default PermissionGuard;